import Quill from 'quill';
import { useEffect, useRef, useCallback, useState } from 'react';
import styled from 'styled-components';

import { usePrevious } from '@hivebrite/react-hooks';

import {
  useBuilderContext,
  useComponentContext,
  useHistoryContext,
  useDeviceModeContext,
} from 'hb-builder/hooks';
import ContentEditable from 'hb-react/shared/components/DesignSystem/QuillEditor/ContentEditable';
import { useQuillEditorContext } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';

const TextEditor = ({
  dataTestId,
  isHeadline = false,
  forceIsSelected,
  initialValue,
  isDisabled,
  onChange,
}) => {
  const { setIsEditorFocus, selectedItemId } = useBuilderContext();
  const { isSelected: defaultIsSelected } = useComponentContext();
  const { cursor, stacksIds } = useHistoryContext();
  const { isDesktopMode } = useDeviceModeContext();

  const [cursorPositions, setCursorPositions] = useState({});

  const isSelected = forceIsSelected || defaultIsSelected;

  const { onEditorEventsRef, quillRef } = useQuillEditorContext();

  const prevCursor = usePrevious(cursor);
  const prevInitialValue = usePrevious(initialValue);
  const prevSelectedItemId = usePrevious(selectedItemId);

  const contentEditableRef = useRef();
  const initialHtmlRef = useRef();

  const handleEditorChange = useCallback(
    (eventName, delta, oldDelta, source) => {
      onEditorEventsRef.current.forEach((event) => event(eventName));
      if (
        eventName === Quill.events.TEXT_CHANGE &&
        (source === 'user' || source === 'silent')
      ) {
        const html = contentEditableRef.current.getHtml();

        onChange(html);
      }
    },
    [],
  );

  const handleFocus = useCallback((e) => {
    e.stopPropagation();
    setIsEditorFocus(true);
  }, []);

  const handleBlur = useCallback((e) => {
    e.stopPropagation();
    setIsEditorFocus(false);
  }, []);

  useEffect(() => {
    if (contentEditableRef.current && isSelected) {
      quillRef.current = contentEditableRef.current.getQuillRef();

      initialHtmlRef.current = contentEditableRef.current.getHtml();
    }
  }, [contentEditableRef.current, isSelected]);

  useEffect(() => {
    const hasCursorChanged = prevCursor !== undefined && prevCursor !== cursor;
    const hasSelectedItemChanged = prevSelectedItemId !== selectedItemId;

    if (quillRef.current && isSelected) {
      if (hasSelectedItemChanged || hasCursorChanged) {
        if (!hasSelectedItemChanged) {
          const delta = quillRef.current.clipboard.convert(initialValue);

          quillRef.current.setContents(delta, 'api');
        }

        quillRef.current.focus();
        const selection = quillRef.current.getSelection();
        const stackId = stacksIds[cursor];
        const cursorPosition = cursorPositions[stackId];

        quillRef.current.setSelection(
          cursorPosition?.index ||
            selection?.index ||
            quillRef.current.getLength(), // fallbacks at the end of the text
          selection?.length || 0, // fallbacks with no selection
          'api',
        );
      } else if (
        quillRef.current.getSelection() === null &&
        prevInitialValue !== undefined &&
        prevInitialValue !== initialValue
      ) {
        const delta = quillRef.current.clipboard.convert(initialValue);

        quillRef.current.setContents(delta, 'api');
      }
    }
  }, [initialValue, cursor, quillRef.current, selectedItemId]);

  useEffect(() => {
    // When a new history state is pushed, we store the text cursor position
    // Later, when the user navigates through the history, we restore the cursor position according to this data
    if (stacksIds?.length && isSelected && quillRef.current) {
      const selection = quillRef.current.getSelection();

      setCursorPositions((prevCursorPositions) => ({
        ...prevCursorPositions,
        [stacksIds[0]]: selection,
      }));
    }
  }, [stacksIds]);

  return (
    <Wrapper
      // Hack to re-render component when device mode is changed in order to have the quillRef updated
      key={isDesktopMode ? 'desktop-mode-key' : 'mobile-mode-key'}
      data-testid={dataTestId}
    >
      <StyledContentEditable
        data-testid="builder-text-content-editable"
        enableIndent
        historyMaxStack={0}
        initialValue={initialValue}
        isDisabled={isDisabled}
        onBlur={handleBlur}
        onChange={handleEditorChange}
        onFocus={handleFocus}
        styleProps={{
          defaultLinkColor: false,
          disableLinks: true,
          isHeadline,
          underlineLinks: true,
        }}
        ref={contentEditableRef}
      />
    </Wrapper>
  );
};

export default TextEditor;

const StyledContentEditable = styled(ContentEditable)`
  .ql-editor {
    overflow-y: unset;
    font-size: inherit !important;
  }
  .ql-blank {
    width: 300px;
  }
`;

const Wrapper = styled.div`
  font-size: inherit;
  pointer-events: all;
  position: relative;
  width: 100%;
`;
